import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import BalbisLogo from "@/components/BalbisLogo";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  useEffect(() => {
    if (user) {
      if (user.role?.startsWith("client_")) navigate("/portal");
      else navigate("/dashboard");
    }
    api.get("/auth/check-setup").then((r) => {
      if (r.data.needs_setup) setIsSetup(true);
    });
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSetup) {
        await api.post("/auth/setup", {
          email: form.email,
          name: form.name,
          password: form.password,
        });
        toast.success("Admin account created! Please log in.");
        setIsSetup(false);
        setForm({ ...form, name: "" });
      } else {
        const u = await login(form.email, form.password);
        if (u.role?.startsWith("client_")) navigate("/portal");
        else navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "#FBF9F6" }}
    >
      <div className="w-full max-w-sm px-6 py-8">
        <div className="mb-8 flex flex-col items-center text-center">
          <BalbisLogo className="h-7 w-auto mb-6" style={{ color: "#292524" }} />

            <h2 className="font-serif text-3xl font-bold mb-2" style={{ color: "#292524" }}>
              {isSetup ? "Welcome" : "Sign in"}
            </h2>
            <p className="text-sm" style={{ color: "#57534E" }}>
              {isSetup
                ? "Create your admin account to get started"
                : "Sign in to Balbis Hub"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {isSetup && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#57534E" }}>
                  Full Name
                </label>
                <input
                  data-testid="setup-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-all focus:ring-2 focus:ring-amber-200"
                  style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#57534E" }}>
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@agency.com"
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-all focus:ring-2 focus:ring-amber-200"
                style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#57534E" }}>
                Password
              </label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPass ? "text" : "password"}
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 pr-10 rounded-lg border text-sm outline-none transition-all focus:ring-2 focus:ring-amber-200"
                  style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: "#A8A29E" }}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg font-medium text-sm transition-all mt-2 disabled:opacity-60"
              style={{ background: "#292524", color: "#fff" }}
            >
              {loading ? "Please wait..." : isSetup ? "Create Account" : "Sign In"}
            </button>
          </form>

          {!isSetup && (
            <p className="text-xs text-center mt-6" style={{ color: "#A8A29E" }}>
              Client? Use the{" "}
              <a href="/login" className="underline" style={{ color: "#D97706" }}>
                Client Portal
              </a>
            </p>
          )}
      </div>
    </div>
  );
}
